"use client";

/**
 * ResumeErrorToast — shows when a task resume fails.
 *
 * Previously, resume failures silently redirected to the home page.
 * Now the user sees a brief error notification explaining what happened.
 * Auto-dismisses after 5 seconds.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useDockVisible } from "@/lib/home-flags-hooks";
import { useTaskStore } from "@/stores/taskStore";
import { AlertCircleIcon } from "@/components/icons/AlertCircleIcon";
import { CloseIcon } from "@/components/icons/CloseIcon";

export function ResumeErrorToast() {
  const dockVisible = useDockVisible(usePathname());
  const resumeError = useTaskStore((s) => s.resumeError);
  const dismissResumeError = useTaskStore((s) => s.dismissResumeError);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (resumeError) {
      timerRef.current = setTimeout(() => {
        dismissResumeError();
      }, 5000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resumeError, dismissResumeError]);

  if (!resumeError) return null;

  return (
    <div
      className={cn(
        "fixed left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 px-4 py-3 rounded-16 text-3.5 shadow-lg bg-palette-red/15 border border-solid border-palette-red/30 text-palette-red backdrop-blur-12 animate-style-interaction-error-toast-inline",
        dockVisible ? "bottom-28" : "bottom-6"
      )}
      role="alert"
    >
      <AlertCircleIcon />
      <span>Could not resume task: {resumeError}</span>
      <button
        onClick={dismissResumeError}
        className="ml-2 p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Dismiss error"
      >
        <CloseIcon width={14} height={14} />
      </button>
    </div>
  );
}
